import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { produce } from 'immer';
import { useViewerStore, actions } from '@store/viewerModule/base';
import dimensionsSelector from '@store/common/dimensionList/selectors';
import { updateDashboardById } from '@store/dashboards/update/actions';
import CustomizeDropdown from '@components/common/CustomizeDropdown';
import commonSelector, { selectedCustomerIdSelector } from '@store/common/selectors';
import vmSelector from '@store/viewerModule/selectors';
import featureNames from '@utils/featureToggle/constants';
import { CDN_ACCOUNT_TYPE, PAGE_TYPE } from '@utils/constants';
import {
  columnsMetrics as defaultColumnsMetrics,
  GROUPBY_MAPPING,
  getColumnsNameReflection,
} from './utils';
import { getSharingState } from '../common/utils';

const mapDimensionGroupBy = (dimension) => {
  return get(GROUPBY_MAPPING, dimension.groupBy, dimension.groupBy);
};

const compare = (columnsNameReflection) => (a, b) =>
  columnsNameReflection[a].toLowerCase() < columnsNameReflection[b].toLowerCase() ? -1 : 1;

const MetricDropMenuWrapper = () => {
  const store = useSelector(vmSelector);
  const [vmStore, dispatch] = useViewerStore();
  const reduxDispatch = useDispatch();
  const baseStore = useSelector(commonSelector);
  const { response: dimensions } = useSelector(dimensionsSelector);
  const selectedCustomerId = useSelector(selectedCustomerIdSelector);
  const columnsNameReflections = React.useMemo(
    () => ({
      ...getColumnsNameReflection(),
      ...Object.fromEntries(dimensions.map((d) => [mapDimensionGroupBy(d), d.name])),
    }),
    [dimensions],
  );
  const customerAccountType = baseStore?.features?.[featureNames.CUSTOMER_ACCOUNT_TYPE];
  const timelineFlag = baseStore.features[featureNames.VIEWER_DRILLDOWN_TIMELINE];
  const metricsMetadata = React.useMemo(() => {
    const next = produce(defaultColumnsMetrics, (draft) => {
      // hide cdn dimension for cdn partner accounts
      if (customerAccountType === CDN_ACCOUNT_TYPE && draft['Interval Playback Metrics']) {
        draft['Interval Playback Metrics'] = draft['Interval Playback Metrics'].filter(
          (dimension) => dimension !== 'IntervalCDN',
        );
      }

      const basics = draft['Basic Information'];
      // Dedupe against every existing column group (not just Basic Information) so a
      // dimension that already exists in another group (e.g. EndedStatus in Lifetime
      // Playback Metrics) is not surfaced a second time.
      const existingColumns = new Set<string>(Object.values(draft).flat() as string[]);
      draft['Basic Information'] = [
        ...basics,
        ...dimensions
          .map((it) => mapDimensionGroupBy(it))
          .filter((it) => !existingColumns.has(it))
          .filter((it) => it !== 'IntervalCDN') // CDN is in the Interval Playback Metrics section already
          .sort(compare(columnsNameReflections)),
      ];
    });

    const keys = Object.keys(next);
    const finalMetricsList = keys.map((key) => {
      const finalMetricList = next[key]
        .filter((v) => {
          // If user does not have timeline permission, we should hidden it on dropmenu
          if (v === 'SessionId' && !timelineFlag) {
            return false;
          }
          return true;
        })
        .map((v) => {
          return Object.assign({}, { name: columnsNameReflections[v], metric: v });
        });
      return { [key]: finalMetricList };
    });

    return Object.assign({}, ...finalMetricsList);
  }, [dimensions, columnsNameReflections, timelineFlag, customerAccountType]);

  const validMetricsSet = React.useMemo(() => {
    const all = Object.values(metricsMetadata || {}).flat();
    return new Set(all.map((x: any) => x.metric));
  }, [metricsMetadata]);

  // Remove from selectedColumns if any column no longer exists in valid set

  React.useEffect(() => {
    const selectedColumns = store?.base?.selectedColumns;
    if (!selectedColumns || !selectedColumns.length || !validMetricsSet.size) return;

    const filteredSelectedColumns = selectedColumns.filter((c) => validMetricsSet.has(c.metric));
    if (filteredSelectedColumns.length === selectedColumns.length) return;

    dispatch(actions.setSelectedColumns({ selectedColumns: filteredSelectedColumns }));

    reduxDispatch(
      actions.setSelectedColumns({
        selectedColumns: filteredSelectedColumns,
        notUpdatePersistence: [PAGE_TYPE.SAVED_DASHBOARD, PAGE_TYPE.SHARING].includes(
          vmStore.pageType,
        ),
      }),
    );
  }, [store?.base?.selectedColumns, validMetricsSet, reduxDispatch, dispatch, vmStore.pageType]);

  const selectedMetrics = React.useMemo(
    () => store.base.selectedColumns.filter((it) => it.show).map((it) => it.metric),
    [store.base.selectedColumns],
  );

  const handleSelectedChange = (value: string[]) => {
    const selectedColumns = value.map((it) => ({ metric: it, show: true }));

    reduxDispatch(
      actions.setSelectedColumns({
        selectedColumns,
        // If it's a saved or sharing VM dashboard, shouldn't update the persistence.
        notUpdatePersistence: [PAGE_TYPE.SAVED_DASHBOARD, PAGE_TYPE.SHARING].includes(
          vmStore.pageType,
        ),
      }),
    );
    dispatch(actions.setSelectedColumns({ selectedColumns }));

    // Need to save the selected columns to saved dashboard config.
    if (PAGE_TYPE.SAVED_DASHBOARD === vmStore.pageType) {
      // First get the next dashboard config.
      const { state: nextDashboardConfig } = getSharingState(
        vmStore.viewerId,
        vmStore.interval,
        baseStore.interval,
        selectedCustomerId,
        selectedColumns.filter((it) => it.show).map((it) => it.metric),
      );

      // Dispatch the latest changes to save to persistence configs.
      reduxDispatch(updateDashboardById(vmStore.id, undefined, nextDashboardConfig));
    }
  };
  const handleAnchorChange = (value: string) => {
    dispatch(actions.setAnchoredColumns({ anchoredColumns: value }));
  };

  return (
    <CustomizeDropdown
      allMetrics={metricsMetadata}
      mandatoryDimensions={defaultColumnsMetrics.Mandatory}
      anchoredMetrics={vmStore.anchoredColumns}
      selectedMetrics={selectedMetrics}
      maxAnchorCount={3}
      onSelectedMetricsChange={handleSelectedChange}
      onAnchorMetricsChange={handleAnchorChange}
    />
  );
};

export default MetricDropMenuWrapper;
